import { create } from 'zustand';

/**
 * Store principal para gerenciar o estado da aplicação de remoção de background
 *
 * Estados principais:
 * - originalImage: imagem original carregada pelo usuário
 * - mask: máscara de segmentação (gerada pela IA ou editada manualmente)
 * - history: histórico de máscaras para undo/redo
 * - tool: ferramenta atual selecionada (brush/eraser)
 * - brushSize: tamanho do pincel
 * - settings: configurações de refinamento (feather, smooth, expand)
 */
const useAdvancedEditorStore = create((set, get) => ({
  // ==================== ESTADO DA IMAGEM ====================
  originalImage: null,
  originalFile: null, // Arquivo original para reprocessamento
  processedImage: null,
  mask: null,
  isProcessing: false,
  isModelLoaded: false,
  currentModel: 'u2net', // Modelo de IA atual

  // ==================== HISTÓRICO (UNDO/REDO) ====================
  history: [],
  historyIndex: -1,
  maxHistorySize: 10,

  // ==================== FERRAMENTAS ====================
  currentTool: 'eraser-remove', // 'eraser-add' | 'eraser-remove' | 'magic-wand-add' | 'magic-wand-remove'
  brushSize: 20,
  magicWandTolerance: 20, // 0-100: tolerância da varinha mágica

  // ==================== CONFIGURAÇÕES DE REFINAMENTO ====================
  settings: {
    feather: 0, // 0-100: suavização das bordas
    smooth: 0, // 0-100: suavização geral
    expand: 0, // -50 a 50: expandir/contrair máscara
  },

  // ==================== CONFIGURAÇÕES DE PROCESSAMENTO ====================
  processingSettings: {
    alphaMatting: false,
    alphaMattingForegroundThreshold: 240, // 0-255
    alphaMattingBackgroundThreshold: 10,  // 0-255
    alphaMattingErodeSize: 10,            // 0-40
  },

  // ==================== VISUALIZAÇÃO ====================
  viewMode: 'result', // 'result' | 'mask' | 'original'
  showGrid: false,
  zoom: 1,

  // ==================== AÇÕES ====================

  /**
   * Define a imagem original carregada pelo usuário
   */
  setOriginalImage: (image) => set({ originalImage: image }),

  /**
   * Define o arquivo original para reprocessamento
   */
  setOriginalFile: (file) => set({ originalFile: file }),

  /**
   * Define o modelo de IA atual
   */
  setCurrentModel: (model) => set({ currentModel: model }),

  /**
   * Define a máscara de segmentação
   */
  setMask: (mask, addToHistory = true) => {
    const state = get();

    if (addToHistory) {
      // Limpa histórico futuro se estamos no meio da pilha
      const newHistory = state.history.slice(0, state.historyIndex + 1);

      // Adiciona nova máscara ao histórico
      newHistory.push(mask);

      // Limita o tamanho do histórico
      if (newHistory.length > state.maxHistorySize) {
        newHistory.shift();
      }

      set({
        mask,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      });
    } else {
      set({ mask });
    }
  },

  /**
   * Undo: volta para a máscara anterior no histórico
   */
  undo: () => {
    const state = get();
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1;
      set({
        mask: state.history[newIndex],
        historyIndex: newIndex,
      });
    }
  },

  /**
   * Redo: avança para a próxima máscara no histórico
   */
  redo: () => {
    const state = get();
    if (state.historyIndex < state.history.length - 1) {
      const newIndex = state.historyIndex + 1;
      set({
        mask: state.history[newIndex],
        historyIndex: newIndex,
      });
    }
  },

  /**
   * Verifica se pode fazer undo
   */
  canUndo: () => {
    const state = get();
    return state.historyIndex > 0;
  },

  /**
   * Verifica se pode fazer redo
   */
  canRedo: () => {
    const state = get();
    return state.historyIndex < state.history.length - 1;
  },

  /**
   * Define a ferramenta atual
   */
  setCurrentTool: (tool) => set({ currentTool: tool }),

  /**
   * Define o tamanho do pincel
   */
  setBrushSize: (size) => set({ brushSize: size }),

  /**
   * Define a tolerância da varinha mágica
   */
  setMagicWandTolerance: (tolerance) => set({ magicWandTolerance: tolerance }),

  /**
   * Atualiza as configurações de refinamento
   */
  updateSettings: (newSettings) => set((state) => ({
    settings: { ...state.settings, ...newSettings },
  })),

  /**
   * Atualiza as configurações de processamento
   */
  updateProcessingSettings: (newSettings) => set((state) => ({
    processingSettings: { ...state.processingSettings, ...newSettings },
  })),

  /**
   * Define o modo de visualização
   */
  setViewMode: (mode) => set({ viewMode: mode }),

  /**
   * Define o estado de processamento
   */
  setIsProcessing: (isProcessing) => set({ isProcessing }),

  /**
   * Define se o modelo está carregado
   */
  setIsModelLoaded: (isModelLoaded) => set({ isModelLoaded }),

  /**
   * Define a imagem processada
   */
  setProcessedImage: (image) => set({ processedImage: image }),

  /**
   * Define o zoom
   */
  setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(5, zoom)) }),

  /**
   * Toggle grid
   */
  toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),

  /**
   * Reseta o editor para o estado inicial
   */
  reset: () => set({
    originalImage: null,
    originalFile: null,
    processedImage: null,
    mask: null,
    history: [],
    historyIndex: -1,
    currentTool: 'brush',
    brushSize: 20,
    magicWandTolerance: 20,
    currentModel: 'u2net',
    settings: {
      feather: 0,
      smooth: 0,
      expand: 0,
    },
    viewMode: 'result',
    showGrid: false,
    zoom: 1,
  }),
}));

export default useAdvancedEditorStore;
